import React from 'react';
import type { ActivityItem } from '../../types/dashboard';

/**
 * Format timestamp to relative time (e.g., "2 hours ago", "Yesterday")
 * More user-friendly than raw timestamps
 */
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) === 1 ? '' : 's'} ago`;
  return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) === 1 ? '' : 's'} ago`;
};

interface ActivityCardProps {
  activity: ActivityItem;
}

// Map event types to accent colors for visual variety
const getActivityAccent = (eventType: string): string => {
  if (eventType.includes('Match')) return 'border-l-primary-500';
  if (eventType.includes('Conversation')) return 'border-l-green-500';
  if (eventType.includes('Persona') || eventType.includes('Summary')) return 'border-l-yellow-500';
  if (eventType.includes('Onboarding')) return 'border-l-theme-blue-500';
  if (eventType.includes('Profile')) return 'border-l-secondary-500';
  return 'border-l-gray-400';
};

const ActivityCard = ({ activity }: ActivityCardProps) => {
  const displayLabel = activity.label || activity.event_type;
  const accent = getActivityAccent(activity.event_type);

  return (
    <div className={`mb-3 bg-gray-100 py-4 px-[.875rem] rounded-lg text-sm font-medium flex gap-3 justify-between items-center border-l-4 ${accent}`}>
      <p className='text-body-500 mb-0 grow'>{displayLabel}</p>
      <p className='text-body-400 mb-0 text-xs whitespace-nowrap'>{getRelativeTime(activity.created_at)}</p>
    </div>
  );
};

export default ActivityCard;
